/**
 * ShortcutHelp — modal listing every keyboard shortcut Opus binds.
 *
 * Controlled by the caller (TopBar renders the trigger button and owns the
 * open state). Also listens for `?` globally so the keyboard shortcut still
 * works anywhere outside an input.
 *
 * Uses Radix Dialog (not a native <dialog>) so the backdrop, focus-trap,
 * and close-on-Esc behaviour are handled consistently with the rest of the
 * app and are not affected by Tailwind's CSS preflight.
 */
import { useEffect } from "react";
import { Keyboard } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

interface ShortcutHelpProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Shortcut {
  keys: string[];
  description: string;
}

const SHORTCUTS: Shortcut[] = [
  { keys: ["⌘", "K"],         description: "Toggle the AI agent drawer" },
  { keys: ["Ctrl", "K"],      description: "(Windows / Linux equivalent of ⌘ K)" },
  { keys: ["Enter"],          description: "Send the current prompt (in agent input)" },
  { keys: ["Shift", "Enter"], description: "New line in the prompt textarea" },
  { keys: ["?"],              description: "Show this help" },
  { keys: ["Esc"],            description: "Close drawer / dialog / help" },
];

export function ShortcutHelp({ open, onOpenChange }: ShortcutHelpProps) {
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const isTyping =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable;
      if (!isTyping && !e.metaKey && !e.ctrlKey && !e.altKey && e.key === "?") {
        e.preventDefault();
        onOpenChange(true);
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <div className="flex items-center gap-2 border-b border-line px-4 py-3 font-semibold">
          <Keyboard size={14} className="text-violet" />
          <DialogTitle className="text-sm">Keyboard shortcuts</DialogTitle>
        </div>
        <ul className="m-0 flex list-none flex-col p-0">
          {SHORTCUTS.map((s) => (
            <li
              key={s.keys.join("+") + s.description}
              className="flex items-center justify-between gap-3 border-t border-line/60 px-4 py-2.5 first:border-t-0"
            >
              <span className="text-[12px] text-fg-dim">{s.description}</span>
              <span className="flex items-center gap-1">
                {s.keys.map((k) => (
                  <kbd
                    key={k}
                    className="inline-flex h-6 min-w-[24px] items-center justify-center rounded border border-line bg-surface-tint px-1.5 font-mono text-[10.5px] text-fg"
                  >
                    {k}
                  </kbd>
                ))}
              </span>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
